import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import {
  type CreateLabInput,
  type CreateLabOrderInput,
  type CreateLabPaymentInput,
  type CreateLabWorkTypeInput,
  type LabBalance,
  type LabOrderAttachment,
  type LabOrderRow,
  type LabOrderStageCounts,
  type LabOrderStageCountsQuery,
  type LabStatement,
  type LabSummary,
  type ListLabOrdersQuery,
  type ListLabsQuery,
  type Paginated,
  type StatementQuery,
  type UpdateLabInput,
  type UpdateLabOrderInput,
  type UpdateLabWorkTypeInput,
} from "@clinic/shared";
import type { ReturnLabOrderInput } from "@clinic/shared";
import { labOrdersApi, labsApi } from "@web/modules/labs/api";
import { uploadLabOrderAttachment } from "@web/shared/api/lab-orders";
import { LAB_ORDERS_KEY } from "@web/shared/constants/query-keys";

export const LABS_KEY = "labs";
export const LAB_BALANCE_KEY = "lab-balance";
export const LAB_STATEMENT_KEY = "lab-statement";
export const LAB_PAYMENTS_KEY = "lab-payments";
export const LAB_WORK_TYPES_KEY = "lab-work-types";

const LAB_KEYS = [LABS_KEY, LAB_ORDERS_KEY, LAB_BALANCE_KEY, LAB_STATEMENT_KEY, LAB_PAYMENTS_KEY];

function useLabMutation<TArgs, TResult>(mutationFn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      for (const key of LAB_KEYS) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

export function useLabs(
  query: Partial<ListLabsQuery> = {},
  enabled = true,
): UseQueryResult<Paginated<LabSummary>> {
  return useQuery({
    queryKey: [LABS_KEY, query],
    queryFn: () => labsApi.list(query),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useLab(id: string): UseQueryResult<LabSummary> {
  return useQuery({
    queryKey: [LABS_KEY, id],
    queryFn: () => labsApi.findOne(id),
    enabled: id !== "",
  });
}

export function useLabWorkTypes(labId: string, includeInactive = false) {
  return useQuery({
    queryKey: [LAB_WORK_TYPES_KEY, labId, includeInactive],
    queryFn: () => labsApi.workTypes(labId, includeInactive),
    placeholderData: (previous) => previous,
    enabled: labId !== "",
  });
}

export function useLabBalance(labId: string): UseQueryResult<LabBalance> {
  return useQuery({
    queryKey: [LAB_BALANCE_KEY, labId],
    queryFn: () => labsApi.balance(labId),
    enabled: labId !== "",
  });
}

export function useLabStatement(
  labId: string,
  query: StatementQuery,
): UseQueryResult<LabStatement> {
  return useQuery({
    queryKey: [LAB_STATEMENT_KEY, labId, query],
    queryFn: () => labsApi.statement(labId, query),
    placeholderData: (previous) => previous,
    enabled: labId !== "",
  });
}

export function useLabOrder(id: string): UseQueryResult<LabOrderRow> {
  return useQuery({
    queryKey: [LAB_ORDERS_KEY, "one", id],
    queryFn: () => labOrdersApi.findOne(id),
    enabled: id !== "",
  });
}

export function useLabOrderStages(
  query: Partial<LabOrderStageCountsQuery> = {},
): UseQueryResult<LabOrderStageCounts> {
  return useQuery({
    queryKey: [LAB_ORDERS_KEY, "stages", query],
    queryFn: () => labOrdersApi.stages(query),
    placeholderData: (previous) => previous,
  });
}

export function useLabOrders(
  query: Partial<ListLabOrdersQuery> = {},
  enabled = true,
): UseQueryResult<Paginated<LabOrderRow>> {
  return useQuery({
    queryKey: [LAB_ORDERS_KEY, query],
    queryFn: () => labOrdersApi.list(query),
    enabled,
    placeholderData: (previous) => previous,
  });
}

export function useLabOrderAttachments(orderId: string): UseQueryResult<LabOrderAttachment[]> {
  return useQuery({
    queryKey: [LAB_ORDERS_KEY, orderId, "attachments"],
    queryFn: () => labOrdersApi.attachments(orderId),
    enabled: orderId !== "",
  });
}

export function useCreateLab() {
  return useLabMutation((input: CreateLabInput) => labsApi.create(input));
}

export function useUpdateLab() {
  return useLabMutation(({ id, body }: { id: string; body: UpdateLabInput }) =>
    labsApi.update(id, body),
  );
}

export function useCreateWorkType() {
  return useLabMutation(({ labId, body }: { labId: string; body: CreateLabWorkTypeInput }) =>
    labsApi.createWorkType(labId, body),
  );
}

export function useUpdateWorkType() {
  return useLabMutation(({ id, body }: { id: string; body: UpdateLabWorkTypeInput }) =>
    labsApi.updateWorkType(id, body),
  );
}

export function useCreateLabOrder() {
  return useLabMutation((input: CreateLabOrderInput) => labOrdersApi.create(input));
}

export function useUpdateLabOrder() {
  return useLabMutation(({ id, body }: { id: string; body: UpdateLabOrderInput }) =>
    labOrdersApi.update(id, body),
  );
}

export type LabOrderStep = "send" | "ready" | "receive" | "fit" | "cancel";

export function useLabOrderStep() {
  return useLabMutation(
    ({
      id,
      step,
      reason = "",
      keepCost,
    }: {
      id: string;
      step: LabOrderStep;
      reason?: string;
      keepCost?: boolean;
    }) =>
      step === "cancel" ? labOrdersApi.cancel(id, { reason, keepCost }) : labOrdersApi[step](id),
  );
}

export function useReturnLabOrder() {
  return useLabMutation(({ id, ...body }: { id: string } & ReturnLabOrderInput) =>
    labOrdersApi.returnToLab(id, body),
  );
}

export function useUploadLabOrderAttachment() {
  return useLabMutation(({ orderId, file }: { orderId: string; file: File }) =>
    uploadLabOrderAttachment(orderId, file),
  );
}

export function useDeleteLabOrderAttachment() {
  return useLabMutation(({ orderId, id }: { orderId: string; id: string }) =>
    labOrdersApi.deleteAttachment(orderId, id),
  );
}

export function usePayLab() {
  return useLabMutation((input: CreateLabPaymentInput) => labsApi.pay(input));
}
